import API, { Endpoints } from "services/api";

export interface FetchedDataSet {
    dataSetId: string;
    dataSetName: string;
    dataSetDescription?: string;
    schemaId?: string;
    businessAreaId?: string;
    version?: number;
    [key: string]: unknown;
}

export async function fetchDataSetById(
    tenantId: string,
    businessAreaId: string,
    schemaId: string,
    dataSetId: string,
): Promise<FetchedDataSet | null> {
    const api = new API();
    const result = await api.get(
        `${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/datasets/${dataSetId}`,
        { tenantid: tenantId },
    );
    return result?.data ?? null;
}

export interface DataObjectFetchContext {
    tenantId: string;
    businessAreaId: string;
    schemaId: string;
    dataSetId: string;
}

export async function fetchDataObjectByUuid(
    ctx: DataObjectFetchContext,
    dataObjectId: string,
): Promise<Record<string, unknown> | null> {
    const api = new API();
    const result = await api.get(
        `${Endpoints.datasets}/api/v1/businessareas/${ctx.businessAreaId}/schemas/${ctx.schemaId}/datasets/${ctx.dataSetId}/dataobjects/${dataObjectId}`,
        { tenantid: ctx.tenantId },
    );
    return result?.data ?? null;
}

export interface DataObjectRevision {
    data?: Record<string, any>;
    metadata?: { id?: string; version?: number; transactionId?: string };
    changes?: { added?: Record<string, unknown>; updated?: Record<string, unknown>; deleted?: Record<string, unknown> };
    previousChanges?: { added?: Record<string, unknown>; updated?: Record<string, unknown>; deleted?: Record<string, unknown> };
    [key: string]: unknown;
}

export async function fetchDataObjectHistory(
    ctx: DataObjectFetchContext,
    dataObjectId: string,
): Promise<DataObjectRevision[]> {
    const api = new API();
    const result = await api.get(
        `${Endpoints.datasets}/api/v1/businessareas/${ctx.businessAreaId}/schemas/${ctx.schemaId}/datasets/${ctx.dataSetId}/dataobjects/${dataObjectId}/history`,
        { tenantid: ctx.tenantId },
    );
    const items = (result?.data as any)?.Items;
    return Array.isArray(items) ? (items as DataObjectRevision[]) : [];
}

// --- Sibling data sets under the same parent schema (cross-dataset comparison) ---

export interface SiblingDataSet {
    dataSetId: string;
    dataSetName?: string;
    schemaId?: string;
    schemaName?: string;
    businessAreaId?: string;
    recordCount?: number;
    erroredRecordsCount?: number;
    runningJobsCount?: number;
    updatedDate?: string;
    createdDate?: string;
    metadata?: { rating?: number; tags?: Record<string, string[]> } | null;
    [key: string]: unknown;
}

export interface SiblingDataSetsPage {
    items: SiblingDataSet[];
    /** Present when the schema has more data sets than this page returned. */
    pageKey?: string;
}

// List every data set that hangs off the SAME parent schema. This is the exact
// endpoint the Schema view's dataset list uses, so the assistant sees the same
// numbers the user would see there.
export async function fetchDataSetsBySchemaId(
    tenantId: string,
    businessAreaId: string,
    schemaId: string,
): Promise<SiblingDataSetsPage> {
    const api = new API();
    const result = await api.get(
        `${Endpoints.datasets}/api/v1/businessareas/${businessAreaId}/schemas/${schemaId}/datasets`,
        { tenantid: tenantId },
    );
    const data = (result?.data as any) || {};
    const items = Array.isArray(data.Items) ? (data.Items as SiblingDataSet[]) : [];
    return { items, pageKey: data.pageKey };
}

export interface FieldInsightItem {
    fieldId?: string;
    fieldName?: string;
    successDataCount?: number;
    erroredDataCount?: number;
    profilingStats?: {
        totalRecords?: number;
        uniquenessPct?: number;
        completePct?: number;
        nullPct?: number;
        skipped?: boolean;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

// Per-field profiling insights for ONE data set (same endpoint the current
// dataset's profilingStats context block is built from).
export async function fetchDataSetFieldInsights(tenantId: string, dataSetId: string): Promise<FieldInsightItem[]> {
    const api = new API();
    const result = await api.get(`${Endpoints.datasets}/api/v1/datasets/${dataSetId}/fieldinsights`, { tenantid: tenantId });
    const items = (result?.data as any)?.Items;
    return Array.isArray(items) ? (items as FieldInsightItem[]) : [];
}

// --- Golden-thread / picklist context (item 2) ---

export interface BusinessAreaDataDefinition {
    dataDomainId: string;
    domainName?: string;
    businessAreaName?: string;
    domainDescription?: string;
    domainPurpose?: string;
    recordCount?: number;
    [key: string]: unknown;
}

export interface DomainDataEntry {
    code: string;
    description: string;
}

// Fetch the Business Area Data (data domain) governance record that a dynamic
// picklist field links to via its dataDomainId.
export async function fetchBusinessAreaDataById(
    tenantId: string,
    dataDomainId: string,
): Promise<BusinessAreaDataDefinition | null> {
    const api = new API();
    const result = await api.get(
        `${Endpoints.datasets}/api/v1/datadomains/${dataDomainId}`,
        { tenantid: tenantId },
    );
    return (result?.data as BusinessAreaDataDefinition) ?? null;
}

// Fetch the code -> description list (the actual picklist values) for a data domain.
export async function fetchDomainData(
    tenantId: string,
    dataDomainId: string,
): Promise<DomainDataEntry[]> {
    const api = new API();
    const result = await api.get(
        `${Endpoints.datasets}/api/v1/datadomains/${dataDomainId}/domaindata`,
        { tenantid: tenantId },
    );
    const items = (result?.data as any)?.Items;
    return Array.isArray(items) ? (items as DomainDataEntry[]) : [];
}
